import { useRouter } from 'next/router';
import { useState, useEffect } from 'react';
import { postData } from '@/utils/helpers';
import { getStripe } from '@/utils/stripe-client';
import { useUser } from '@/utils/useUser';
import Button from '@/components/Button';
import PricingFeatures from '@/components/PricingFeatures';
import { motion } from 'framer-motion';
import { SparklesIcon } from '@heroicons/react/24/solid';
import { supabase } from '@/utils/supabase-client';
import { useTranslations } from 'next-intl';
import createApp from '@shopify/app-bridge';
import { Redirect } from '@shopify/app-bridge/actions';


function getShopifyCtxFromUrl() {
  if (typeof window === "undefined") return null;
  const p = new URLSearchParams(window.location.search);
  const host = p.get("host");
  const shop = p.get("shop");
  const embedded = p.get("embedded");
  const company_id = p.get("company_id");
  const shopify_variant = p.get("shopify_variant");

  // Якщо ми в iframe Shopify (або маємо ці параметри), повертаємо контекст
  if (shop && (host || embedded === "1")) {
    return {
      host: host || '',
      shop,
      embedded: "1",
      company_id: company_id || null,
      shopify_variant: shopify_variant || null
    };
  }
  return null;
}

export const Pricing = ({ products }) => {
  const t = useTranslations('pricing');
  const router = useRouter();
  const { session, user, planDetails } = useUser();
  const [priceIdLoading, setPriceIdLoading] = useState(false);
  const [hasTrial, setHasTrial] = useState(false);
  const [loadingTrialStatus, setLoadingTrialStatus] = useState(true);

  useEffect(() => {
    const checkTrialStatus = async () => {
      if (!user?.id) {
        setLoadingTrialStatus(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('subscriptions')
          .select('status, trial_start, trial_end')
          .eq('user_id', user.id);

        if (error) throw error;

        const hadTrial = data?.some(
          (sub) => sub.status === 'trialing' || (sub.trial_start && sub.trial_end)
        );

        setHasTrial(hadTrial);
      } catch (error) {
        console.error('Error checking trial status:', error);
      } finally {
        setLoadingTrialStatus(false);
      }
    };

    checkTrialStatus();
  }, [user]);

  const handleCheckout = async (price) => {
    if (!session) {
      router.push('/signup');
      return;
    }

    setPriceIdLoading(price);
    await createCheckoutSession(price);
  };

  const createCheckoutSession = async (priceId) => {
    try {
      const shopifyCtx = getShopifyCtxFromUrl();

      const data = {
        price: priceId,
        shopify: shopifyCtx,
      };

      const resp = await postData({
        url: "/api/create-checkout-session",
        data,
        token: session?.access_token,
      });

      const sessionId = resp.sessionId;
      const checkoutUrl = resp.checkoutUrl;

      // ✅ Shopify Billing: використовуємо App Bridge для навігації з iframe
      // if (sessionId === 'shopify_billing' && checkoutUrl) {
      //   const apiKey = process.env.NEXT_PUBLIC_SHOPIFY_API_KEY;
      //   const host = resp.host
      //     || new URLSearchParams(window.location.search).get('host')
      //     || shopifyCtx?.host;
      //
      //   if (apiKey && host) {
      //     const app = createApp({ apiKey, host, forceRedirect: true });
      //     Redirect.create(app).dispatch(Redirect.Action.REMOTE, checkoutUrl);
      //   } else {
      //     // Fallback якщо App Bridge недоступний
      //     window.open(checkoutUrl, '_blank');
      //   }
      //   return;
      // }
      // ✅ Shopify Managed Pricing: відкриваємо Shopify-hosted pricing page з iframe через App Bridge
      if ((sessionId === 'shopify_managed_pricing' || sessionId === 'shopify_billing') && checkoutUrl) {
        const p = new URLSearchParams(window.location.search);
        const variant = (p.get('shopify_variant') || '').toLowerCase();
        const apiKey = variant === 'custom'
          ? process.env.NEXT_PUBLIC_SHOPIFY_API_KEY_CUSTOM
          : (process.env.NEXT_PUBLIC_SHOPIFY_API_KEY_PUBLIC || process.env.NEXT_PUBLIC_SHOPIFY_API_KEY);

        // host беремо з відповіді бекенду або з URL
        const host =
          resp.host ||
          p.get('host') ||
          shopifyCtx?.host;
        const inIframe = (() => {
          try {
            return window.self !== window.top;
          } catch (_) {
            return true;
          }
        })();

        if (apiKey && host && inIframe) {
          try {
            const app = createApp({ apiKey, host, forceRedirect: true });
            Redirect.create(app).dispatch(Redirect.Action.REMOTE, checkoutUrl);
            // Fallback: if App Bridge redirect doesn't navigate away within 1.5s,
            // force a top-level redirect. Cancel if we detect navigation started.
            let navigated = false;
            const onLeave = () => { navigated = true; };
            window.addEventListener('beforeunload', onLeave, { once: true });
            window.addEventListener('pagehide', onLeave, { once: true });
            window.setTimeout(() => {
              window.removeEventListener('beforeunload', onLeave);
              window.removeEventListener('pagehide', onLeave);
              if (navigated) return;
              try {
                window.open(checkoutUrl, '_top');
                return;
              } catch (_) { }
              try {
                if (window.top && window.top.location) {
                  window.top.location.assign(checkoutUrl);
                }
              } catch (_) { }
            }, 1500);
          } catch (_) {
            alert('Shopify redirect failed in embedded mode. Please reload the app in Shopify Admin and try again.');
          }
        } else {
          // Non-embedded fallback
          try {
            if (window.top && window.top.location) {
              window.top.location.assign(checkoutUrl);
              return;
            }
          } catch (_) { }
          window.location.assign(checkoutUrl);
        }
        return;
      }


      // ✅ Звичайний Afivio (не Shopify): як і зараз
      const stripe = await getStripe();
      const { error } = await stripe.redirectToCheckout({ sessionId });
      if (error) throw error;
    } catch (error) {
      console.error("Checkout redirect error:", error);
      alert(error.message || t("errors.checkoutFallback"));
    } finally {
      setPriceIdLoading(false);
    }
  };


  const sortedProducts = products?.sort(
    (a, b) =>
      (a.prices[0]?.unit_amount || Infinity) - (b.prices[0]?.unit_amount || Infinity)
  );

  const getButtonText = (product) => {
    const isCustom = product.name === 'Galaxy';

    if (planDetails === product.name) return t('cta.currentPlan');
    if (isCustom) return t('cta.contactSales');
    if (priceIdLoading === product.prices[0]?.id) return t('cta.processing');

    if (loadingTrialStatus) return t('cta.loading');
    return hasTrial ? t('cta.getPlan', { plan: product.name }) : t('cta.startFreeTrial');
  };

  return (
    <section className="relative">
      <div className="max-w-7xl mx-auto px-6 text-center">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {sortedProducts?.map((product, idx) => {
            const isPopular = product.name === 'Boost';
            const isCustom = product.name === 'Galaxy';

            const priceString = product.prices[0]?.unit_amount
              ? new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: product.prices[0].currency,
              }).format(product.prices[0].unit_amount / 100)
              : null;

            return (
              <motion.div
                key={product.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.08 }}
                className={`relative flex flex-col rounded-2xl border ${isPopular
                  ? 'bg-gradient-to-br from-indigo-600 to-blue-600 text-white shadow-xl scale-105'
                  : 'bg-white border-gray-200 shadow-sm'
                  }`}
              >
                {isPopular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className="flex items-center gap-2 bg-yellow-400 text-gray-900 px-3 py-1 rounded-full text-sm font-medium shadow">
                      <SparklesIcon className="w-4 h-4" />
                      <span>{t('badge.mostPopular')}</span>
                    </div>
                  </div>
                )}

                <div className="p-8 text-left">
                  <h3 className={`text-2xl font-bold ${isPopular ? 'text-white' : 'text-gray-900'}`}>
                    {product.name}
                  </h3>
                  <p className={`mt-2 text-sm ${isPopular ? 'text-indigo-100' : 'text-gray-500'}`}>
                    {product.description || t('card.descriptionFallback')}
                  </p>
                </div>

                <div
                  className={`px-8 py-6 border-t ${isPopular ? 'border-white/20' : 'border-gray-100'
                    }`}
                >
                  <div className="flex items-baseline justify-center">
                    {isCustom ? (
                      <span
                        className={`text-3xl font-semibold ${isPopular ? 'text-white' : 'text-gray-900'
                          }`}
                      >
                        {t('card.customPrice')}
                      </span>
                    ) : (
                      <>
                        <span
                          className={`text-4xl font-extrabold ${isPopular ? 'text-white' : 'text-gray-900'
                            }`}
                        >
                          {priceString}
                        </span>
                        <span className={`ml-1 ${isPopular ? 'text-indigo-100' : 'text-gray-500'}`}>
                          {t('card.perMonth')}
                        </span>
                      </>
                    )}
                  </div>
                  {!isCustom && (
                    <p
                      className={`mt-2 text-sm text-center ${isPopular ? 'text-indigo-100/80' : 'text-indigo-600'
                        }`}
                    >
                      {t('card.trialNote')}
                    </p>
                  )}
                </div>

                <div className="px-8 py-6 flex-1 text-left">
                  <PricingFeatures productName={product.name} isPopular={isPopular} />
                </div>

                <div className="px-8 pb-8">
                  <Button
                    className={`relative inline-flex items-center justify-center rounded-md font-semibold focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all ${isPopular
                      ? ' text-blue-500 bg-white hover:bg-white rounded-xl hover:scale-105 transition-transform'
                      : ' text-white bg-secondary hover:bg-secondary-2 rounded-xl hover:scale-105 transition-transform'
                      }`}
                    disabled={planDetails === product.name || loadingTrialStatus}
                    onClick={() => {
                      if (isCustom) {
                        router.push('/contact');
                      } else {
                        handleCheckout(product.prices[0]?.id);
                      }
                    }}
                  >
                    {getButtonText(product)}
                  </Button>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Pricing;
